import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Folio, Engraving, Glossarium, Marginalia, StaffExample } from "@/components/sn/SNShell";
import { Notation } from "@/components/Notation";

export default function StaffNotation3() {
  const { lesson, course, accent } = findLesson("staff-notation", "3");
  return (
    <LessonShell
      course={course}
      courseSlug="staff-notation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/staff-notation/SN3-Bass-Clef-Animated.mp4"
        caption="The bass clef — 5 lines (GBDFA) + 4 spaces (ACEG)"
        hint="≈ 24 sec · watch first"
      />
      <Folio number="I" title="The F clef — and how to draw it">
        <p>
          The bass clef has a sign. Drawing it is "very easy, come to think of it," Jason says.
          You put a dot on the <strong>fourth line from the bottom</strong> — that dot names
          the line as F. Then you make a curved mark coming off that dot, and finish with two
          more dots, one in the space above that fourth line and one in the space below it,
          bracketing the F line itself.
        </p>
        <p>
          So the bass clef is also called the <strong>F clef</strong>, for the same reason the
          treble clef is called the G clef: the dot identifies a specific line by name. In the
          bass clef, the fourth line is F. Every other note on the bass staff is found by
          counting in thirds from that F.
        </p>
        <Marginalia>
          The two clefs sit one above the other on a grand staff. The treble clef handles the
          higher range; the bass clef handles the lower range. Together they cover the full
          range of the piano — and most other instruments fall somewhere within that range.
        </Marginalia>
      </Folio>

      <Folio number="II" title="Lines of the bass clef — G B D F A">
        <p>
          The five lines of the bass clef from bottom to top are{" "}
          <strong>G – B – D – F – A</strong>. Notice the pattern: these are also thirds apart,
          exactly like the treble clef. G to B (third), B to D (third), D to F (third),
          F to A (third). The interval logic is identical — because it's the same system.
        </p>
        <p>
          But Jason gives an important warning: this G is not the G above middle C. This is
          a <strong>very low G</strong> — the G below middle C, near the bottom of the piano.
          As you add ledger lines below the bass clef, you reach even lower: "I'm almost
          getting to the end of the piano now," Jason says, pointing to where E sits on the
          first ledger line below the staff. Two ledger lines down and you are on C — deep
          bass territory.
        </p>

        <StaffExample caption="The five line notes of the bass clef — G, B, D, F, A — each a third apart.">
          <Notation
            measures={[
              { chords: [{ keys: ["G2"], duration: "w" }] },
              { chords: [{ keys: ["B2"], duration: "w" }] },
              { chords: [{ keys: ["D3"], duration: "w" }] },
              { chords: [{ keys: ["F3"], duration: "w" }] },
              { chords: [{ keys: ["A3"], duration: "w" }] },
            ]}
            timeSig="4/4"
            clef="bass"
            width={620}
            height={140}
          />
        </StaffExample>
      </Folio>

      <Folio number="III" title="Spaces of the bass clef — A C E G">
        <p>
          The four spaces of the bass clef from bottom to top are{" "}
          <strong>A – C – E – G</strong>. Mnemonic: "All Cows Eat Grass" — like what they
          do. Again, thirds throughout: A to C (third), C to E (third), E to G (third).
        </p>
        <p>
          Above the staff you can add ledger line notes: B above the top line, then D, using
          the same third-counting pattern. Below the bottom line you have F, then D, using
          ledger lines going downward. The system is identical to the treble clef — just
          centred on different pitches.
        </p>

        <StaffExample caption="The four space notes of the bass clef — A, C, E, G.">
          <Notation
            measures={[
              { chords: [{ keys: ["A2"], duration: "w" }] },
              { chords: [{ keys: ["C3"], duration: "w" }] },
              { chords: [{ keys: ["E3"], duration: "w" }] },
              { chords: [{ keys: ["G3"], duration: "w" }] },
            ]}
            timeSig="4/4"
            clef="bass"
            width={520}
            height={140}
          />
        </StaffExample>
      </Folio>

      <Engraving
        src="/notes/staff-notation/finals/03-notes-on-the-bass-clef-page1.png"
        caption="Jason's bass clef map. Lines G B D F A; spaces A C E G. Middle C appears one ledger line above — the meeting point of both clefs."
      />

      <Folio number="IV" title="Don't confuse the clefs — read the sign">
        <p>
          Here is a very common mistake in reading a grand staff: looking at a note
          in the bass clef and calling it by its treble name. A note on the bottom line of the
          bass clef is G. The same position on the treble clef is E. They look identical on the
          staff. The only difference is the clef sign at the left edge.
        </p>
        <p>
          "Don't get confused," Jason says. "You have to read the clef." Make it a habit: before
          reading any note, check which clef you're in. This is the first thing your eye should
          land on at the start of any line of music.
        </p>

        <Glossarium>
          <p>
            <strong>Bottom line of treble clef</strong> → E
          </p>
          <p>
            <strong>Bottom line of bass clef</strong> → G (a lower G, not the treble G)
          </p>
          <p>
            Careful: it is the <em>names</em> that differ by a third, not the pitches — the
            bass G is more than an octave below the treble E. But the shift is a useful trick
            for reading. Take any position on the staff: the bass-clef name is a third
            <strong>above</strong> the treble-clef name for the same spot (G against E, B
            against G, D against B). So reading a bass line with treble eyes, come
            <strong>down</strong> a third.
          </p>
        </Glossarium>
      </Folio>

      <Folio number="V" title="Middle C — where the two clefs meet">
        <p>
          Middle C is the note that sits exactly between the treble and bass clefs. On the
          treble clef, it sits on <strong>one ledger line below</strong> the staff. On the
          bass clef, it sits on <strong>one ledger line above</strong> the staff. It is the
          same pitch written from two different perspectives.
        </p>
        <p>
          Jason mentions that "the piano historically perhaps started this whole thing" — the
          piano's range was so large that notation needed both a high-range clef and a
          low-range clef to cover it. The treble and bass clefs, placed together as a grand
          staff, cover essentially the full piano keyboard. If the notation system can serve
          the piano, it can serve a violin, a vocalist, a saxophone — because every other
          instrument's range fits within the piano's.
        </p>
        <Marginalia>
          When you sit at the piano, Jason's guideline is to position yourself with middle C
          roughly at your centre — in front of your belly button, not your right hand. This
          puts your hands in the natural position to use both clefs equally.
        </Marginalia>
      </Folio>
    </LessonShell>
  );
}
